import FullCalendar, { Calendar, CalendarOptions } from '@fullcalendar/vue';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import scrollGrid from '@fullcalendar/scrollgrid';
import { ParsedEvent, ParsedResource } from '@bookings/interfaces/calendar';
import Datepicker from '@shared/components/forms/inputs/datepicker/datepicker.vue';
import moment from 'moment';

@Component({
  name: 'bpc-resource-calendar',
  components: {
    'full-calendar': FullCalendar,
    'bpc-datepicker': Datepicker,
  },
})
export default class ResourceCalendar extends Vue {
  $refs: {
    fcalendar: InstanceType<typeof FullCalendar> & {
      $emit(event): void;
      getApi(): Calendar;
    };
  };

  @Prop(String) readonly date: string;
  @Prop(Array) readonly events: ParsedEvent[];
  @Prop(Array) readonly resources: ParsedResource[];
  @Prop(Boolean) readonly withDatepicker: boolean;
  @Prop({ type: Boolean, default: false }) readonly calendarSelectable: boolean;
  @Prop(Boolean) readonly showArrows: boolean;
  @Prop({ type: String, default: 'w-100' }) readonly arrowGroupClass: string;
  @Prop(String) readonly slotMinTime: string;
  @Prop(String) readonly slotMaxTime: string;
  @Prop(String) readonly slotDuration: string;

  headingDate: string = '';

  get currentSlotDuration(): string {
    if (!this.slotDuration) {
      const slotDuration =
        this.$store.getters['centerSettings/centerSettings']?.default_slot_duration || 30;
      const hours = Math.floor(slotDuration / 60);
      const minutes = slotDuration % 60 < 10 ? `0${slotDuration % 60}` : slotDuration % 60;

      return `${hours}:${minutes}:00`;
    }
    return this.slotDuration;
  }

  get currentSlotMinTime(): string {
    if (!this.slotMinTime) {
      return this.$store.getters['centerSettings/centerSettings'].default_start_hour;
    }
    return this.slotMinTime;
  }

  get currentSlotMaxTime(): string {
    if (!this.slotMinTime) {
      return this.$store.getters['centerSettings/centerSettings'].default_end_hour;
    }
    return this.slotMinTime;
  }

  get language(): string {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].default_language;
    }
    return null;
  }

  get selectedDate(): string {
    return this.date || new Date().toISOString().substr(0, 10);
  }

  set selectedDate(val) {
    this.$emit('date-change', val);
  }

  calendarOptions: CalendarOptions = {
    longPressDelay: 200,
    eventLongPressDelay: 200,
    selectLongPressDelay: 200,
    plugins: [resourceTimeGridPlugin, interactionPlugin, scrollGrid],
    initialView: 'resourceTimeGrid',
    schedulerLicenseKey: process.env.VUE_APP_FULLCALENDAR_LICENCE_KEY,
    height: 'auto',
    resources: [],
    initialDate: this.selectedDate,
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      omitZeroMinute: false,
      hour12: false,
      meridiem: false,
    },
    slotLabelFormat: {
      hour: '2-digit',
      minute: '2-digit',
      omitZeroMinute: false,
      hour12: false,
      meridiem: false,
    },
    eventOverlap: true,
    datesAboveResources: true,
    dateClick: this.handleSlotClicked,
    select: this.handleDateSelect,
    eventClick: this.handleEventClick,
    eventDrop: this.handleEventDrop,
    eventResize: this.handleEventResize,
    slotDuration: this.currentSlotDuration,
    selectOverlap: this.handleConflictEvents,
    selectable: this.calendarSelectable,
    dayMinWidth: 100,
    allDaySlot: false,
    slotMinTime: this.currentSlotMinTime,
    slotMaxTime: this.currentSlotMaxTime,
    nowIndicator: true,
  };

  handleConflictEvents(e) {
    const res =
      e.classNames.includes('my-event') ||
      e.classNames.includes('unavailable-event') ||
      e.classNames.includes('slot-not-available');
    if (res) {
      this.$emit('slot-not-available', e);
      // this.$bpcMessage.showErrorMessage({ text: 'bookings.exclusion_error' });
    }
    return !res;
  }

  /* Lifecycle hooks */
  // created () { }
  mounted() {
    this.headingDate = this.date
      ? this.updateHeadingDate(this.date)
      : this.updateHeadingDate(moment.now());
    this.renderCalendar();
  }

  renderCalendar() {
    this.$set(this.calendarOptions, 'events', this.events);
    this.$set(this.calendarOptions, 'resources', this.resources);
    this.$refs.fcalendar.$emit('render');
  }

  renderEvents() {
    this.$refs.fcalendar.getApi().unselect();
    this.$set(this.calendarOptions, 'events', this.events);
    this.$set(this.calendarOptions, 'resources', this.resources);
    this.$refs.fcalendar.$emit('rerender-events');
  }

  handleSlotClicked(info: DateClickArg) {
    this.$emit('slot-clicked', info);
  }

  handleDateSelect(event) {
    this.$emit('date-select', event);
  }

  handleEventClick(event) {
    this.$emit('event-click', event);
  }

  handleEventDrop(event) {
    this.$emit('event-drop', event);
  }

  handleEventResize(event) {
    this.$emit('event-resize', event);
  }

  updateHeadingDate(date) {
    const locale = this.language === 'be' ? 'fr' : this.language;
    return moment(date)
      .locale(locale)
      .format('dddd DD/MM/YYYY')
      .toUpperCase();
  }

  @Watch('events', { deep: true })
  onEventsChange() {
    this.renderEvents();
  }

  @Watch('resources', { deep: true })
  onResourceChange() {
    this.renderCalendar();
  }

  @Watch('date')
  onDateChange() {
    this.selectedDate = this.date;
    this.headingDate = this.updateHeadingDate(this.date);
    this.$refs.fcalendar.getApi().gotoDate(this.selectedDate);
  }

  @Watch('currentSlotMinTime')
  onCurrentSlotMinTimeChange() {
    this.renderCalendar();
  }

  @Watch('currentSlotMaxTime')
  onCurrentSlotMaxTimeChange() {
    this.renderCalendar();
  }

  @Watch('currentSlotDuration')
  onCurrentSlotDurationChange() {
    this.renderCalendar();
  }

  // updated () { }
  // destroyed () { }
}
